import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import FormPanel from "../components/FormPanel";
import VisualPanel from "../components/VisualPanel";
import "../style/App.css";
import { updateToc, fetchTocProjectById, requestExpertReview } from "../services/api";
import {
  FORM_FIELD_LABELS,
  TOC_CANONICAL,
  buildTocColorPayload,
  buildTocDataPayload,
  readProgramName,
  readTocColorSection,
  parseExternalCloudColors,
  DEFAULT_PUZZLE_COLOR,
  buildPuzzleColorsForSave,
  storageScalarToFormField,
  storageArrayToFormField,
  storageArrayToExternalFormField,
} from "../constants/tocFields";

// Main data structure for Theory of Change project
export type Data = {
  projectTitle: string;
  goal: string;
  aim: string;
  activities: string;
  objectives: string;
  externalInfluences: string;
};
export type ColumnColors = {
  [field in keyof Data]?: { bg: string; text: string };
};

export type CloudColor = { bg: string; text: string };

// Toast notification component - displays temporary messages
const Toast = ({
  message,
  type = "success",
  onClose,
}: {
  message: string;
  type?: "success" | "error" | "warning";
  onClose: () => void;
}) => {
  // Auto-dismiss after 3 seconds
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const icons = {
    success: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="10"
          cy="10"
          r="9"
          stroke="currentColor"
          strokeWidth="2"
          fill="none"
        />
        <path
          d="M6 10L9 13L14 7"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    error: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="10"
          cy="10"
          r="9"
          stroke="currentColor"
          strokeWidth="2"
          fill="none"
        />
        <path
          d="M7 7L13 13M13 7L7 13"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
    warning: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10 2L2 17h16L10 2z"
          stroke="currentColor"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M10 8v4M10 14v.5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
  };

  return (
    <div className={`toast-notification toast-${type}`}>
      {icons[type]}
      {message}
    </div>
  );
};

function App() {
  // Get project ID from URL params
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const userId = localStorage.getItem("userId");
  const currentProjectId = projectId || localStorage.getItem("projectId");

  // Default color scheme for column cards
  // const defaultColumnColors = {
  //   activities: { bg: "#8C6BDC", text: "#ffffff" },
  //   objectives: { bg: "#A07CFD", text: "#ffffff" },
  //   aim: { bg: "#C074E0", text: "#ffffff" },
  //   goal: { bg: "#8C6BDC", text: "#ffffff" },
  // };

//   const formatArrayField = (value: unknown): string => {
//   if (!Array.isArray(value)) return "";
//   return value.filter(Boolean).join(", ");
// };

  const defaultColumnColors = useMemo(() => ({
  activities: { bg: "#ffffff", text: "#111111" },
  objectives: { bg: "#ffffff", text: "#111111" },
  aim: { bg: "#ffffff", text: "#111111" },
  goal: { bg: "#ffffff", text: "#111111" },
}), []);

  // State management
  const [data, setData] = useState<Data>({
    projectTitle: "",
    goal: "",
    aim: "",
    activities: "",
    objectives: "",
    externalInfluences: "",
  });

  const [columnColors, setColumnColors] = useState<{
    [field in keyof Data]?: { bg: string; text: string };
  }>(defaultColumnColors);

  const [cloudColors, setCloudColors] = useState<
    { bg: string; text: string }[]
  >([{ ...DEFAULT_PUZZLE_COLOR }]);

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightField, setHighlightField] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "warning";
  } | null>(null);
  const [saveStatus, setSaveStatus] = useState<"idle" | "dirty" | "saving" | "saved" | "error">("idle");
  const [lastSavedAt, setLastSavedAt] = useState("");
  const [hasStartedEditing, setHasStartedEditing] = useState(false);
  const [initialProjectTitle, setInitialProjectTitle] = useState("");

  // Normalize 3-character hex codes to 6-character format
  const normalizeHex = (hex: string) => {
    if (/^#[0-9a-f]{3}$/i.test(hex)) {
      return "#" + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
    }
    return hex;
  };

  // Check if a field has actual content
  const hasContent = (value: string): boolean => {
    if (!value || value.trim() === "") return false;

    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.some((item) => item && item.trim() !== "");
      }
      return value.trim() !== "";
    } catch {
      return value.trim() !== "";
    }
  };

  // Validate that all required fields are filled
  const isFormValid = (): boolean => {
  return (
    data.projectTitle.trim() !== "" &&
    hasContent(data.goal) &&
    hasContent(data.aim) &&
    hasContent(data.activities) &&
    hasContent(data.objectives) &&
    hasContent(data.externalInfluences)
  );
};

const getEmptyFields = (): string[] => {
  const emptyFields: string[] = [];
  const fieldNames = FORM_FIELD_LABELS;

  if (data.projectTitle.trim() === "") emptyFields.push(fieldNames.projectTitle);
  if (!hasContent(data.goal)) emptyFields.push(fieldNames.goal);
  if (!hasContent(data.aim)) emptyFields.push(fieldNames.aim);
  if (!hasContent(data.activities)) emptyFields.push(fieldNames.activities);
  if (!hasContent(data.objectives)) emptyFields.push(fieldNames.objectives);
  if (!hasContent(data.externalInfluences)) emptyFields.push(fieldNames.externalInfluences);

  return emptyFields;
};

  // Load project data from API when component mounts or projectId changes
  useEffect(() => {
    const loadProject = async () => {
      const projectIdFromParams =
        projectId || localStorage.getItem("projectId");

      if (!projectIdFromParams) {
        setColumnColors(defaultColumnColors);
        setCloudColors([{ ...DEFAULT_PUZZLE_COLOR }]);
        return;
      }

      setIsLoading(true);
      try {
        const res = await fetchTocProjectById(projectIdFromParams);
        if (res.success && res.data?.projects?.length > 0) {
          const project = res.data.projects[0];
          const toc = project.tocData;
          const colors = project.tocColor || {};

          // setData({
          //   projectTitle: toc.projectTitle || "",
          //   goal: toc.bigPictureGoal || "",
          //   aim: toc.projectAim || "",
          //   beneficiaries: toc.beneficiaries?.description || "",
          //   activities: activitiesString,
          //   objectives: objectivesString,
          //   externalInfluences: externalInfluencesString,
          // });

          const tocRecord = toc as Record<string, unknown>;
          const loadedTitle =
            readProgramName(tocRecord) ||
            (project.projectTitle as string) ||
            "";
          setData({
  projectTitle: loadedTitle,
  goal: storageScalarToFormField(
    tocRecord[TOC_CANONICAL.longTermImpact] ?? tocRecord.bigPictureGoal
  ),
  aim: storageScalarToFormField(
    tocRecord[TOC_CANONICAL.mediumTermImpact] ?? tocRecord.projectAim
  ),
  activities: storageArrayToFormField(
    tocRecord[TOC_CANONICAL.programActivities] ?? toc.activities
  ),
  objectives: storageArrayToFormField(
    tocRecord[TOC_CANONICAL.shortTermImpact] ?? toc.objectives
  ),
  externalInfluences: storageArrayToExternalFormField(
    tocRecord[TOC_CANONICAL.mappingExternalFactors] ?? toc.externalFactors
  ),
});
          setInitialProjectTitle(loadedTitle);

          // Load saved colors or use defaults
          const colorRecord = colors as Record<string, { bg: string; text: string }>;
          const newColumnColors = {
            activities:
              readTocColorSection(
                colorRecord,
                TOC_CANONICAL.programActivities,
                "activities"
              ) || defaultColumnColors.activities,
            objectives:
              readTocColorSection(
                colorRecord,
                TOC_CANONICAL.shortTermImpact,
                "objectives"
              ) || defaultColumnColors.objectives,
            aim:
              readTocColorSection(
                colorRecord,
                TOC_CANONICAL.mediumTermImpact,
                "projectAim"
              ) || defaultColumnColors.aim,
            goal:
              readTocColorSection(
                colorRecord,
                TOC_CANONICAL.longTermImpact,
                "bigPictureGoal"
              ) || defaultColumnColors.goal,
          };
          setColumnColors(newColumnColors);

          // Load cloud colors - handles both array and object formats
          const externalColors =
            colorRecord[TOC_CANONICAL.mappingExternalFactors] ??
            colors.externalFactors;
          setCloudColors(parseExternalCloudColors(externalColors));
        } else {
          setColumnColors({ ...defaultColumnColors });
          setCloudColors([{ ...DEFAULT_PUZZLE_COLOR }]);
        }
      } catch (err) {
        console.error("Failed to load project", err);
        setToast({ message: "Failed to load project", type: "error" });
        setColumnColors({ ...defaultColumnColors });
        setCloudColors([{ ...DEFAULT_PUZZLE_COLOR }]);
      } finally {
        setIsLoading(false);
      }
    };

    loadProject();
  }, [projectId, defaultColumnColors]);

  // const updateField = (field: keyof Data, value: string) => {
  //   setData((prev) => ({ ...prev, [field]: value }));
  // };
  const updateField = (field: keyof Data, value: string) => {
  setHasStartedEditing(true);
  setSaveStatus("dirty");
  setData((prev) => ({ ...prev, [field]: value }));
  };

  const updateColumnColors = (colors: {
  [field in keyof Data]?: { bg: string; text: string };
}) => {
  setHasStartedEditing(true);
  setSaveStatus("dirty");
  setColumnColors(colors);
};

  const updateCloudColors = (colors: { bg: string; text: string }[]) => {
  setHasStartedEditing(true);
  setSaveStatus("dirty");
  setCloudColors(colors);
  };

  const handleFieldAdded = (fieldName: string) => {
    setHighlightField(fieldName);
    setTimeout(() => setHighlightField(null), 2100);
  };

const autoSaveDraft = React.useCallback(async () => {
  const currentProjectId = projectId || localStorage.getItem("projectId");
  if (!currentProjectId) return;

  const isNameChange =
    data.projectTitle.trim() !== initialProjectTitle.trim();

  try {
    setSaveStatus("saving");

    const puzzleColorsForSave = buildPuzzleColorsForSave(
      cloudColors,
      data.externalInfluences
    ).map((c) => ({
      bg: normalizeHex(c.bg),
      text: normalizeHex(c.text),
    }));

    const tocColorPayload = buildTocColorPayload(
      {
        goal: {
          bg: normalizeHex(columnColors.goal?.bg || "#ffffff"),
          text: normalizeHex(columnColors.goal?.text || "#111111"),
        },
        aim: {
          bg: normalizeHex(columnColors.aim?.bg || "#ffffff"),
          text: normalizeHex(columnColors.aim?.text || "#111111"),
        },
        objectives: {
          bg: normalizeHex(columnColors.objectives?.bg || "#ffffff"),
          text: normalizeHex(columnColors.objectives?.text || "#111111"),
        },
        activities: {
          bg: normalizeHex(columnColors.activities?.bg || "#ffffff"),
          text: normalizeHex(columnColors.activities?.text || "#111111"),
        },
      },
      puzzleColorsForSave
    );

    const payload = {
      userId,
      projectId: currentProjectId,
      projectTitle: data.projectTitle,
      updateName: isNameChange,
      status: "draft" as "draft" | "published",
      tocData: buildTocDataPayload(
        {
          goal: data.goal || "",
          aim: data.aim || "",
          objectives: data.objectives,
          activities: data.activities,
          externalInfluences: data.externalInfluences,
        },
        data.projectTitle
      ),
      tocColor: tocColorPayload,
    };

    const result = await updateToc(payload);

    if (result.success) {
      if (isNameChange) {
        setInitialProjectTitle(data.projectTitle.trim());
      }
      setSaveStatus("saved");
      setLastSavedAt(
        new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
      );
    } else {
      setSaveStatus("error");
    }
  } catch (err) {
    console.error("Autosave error:", err);
    setSaveStatus("error");
  }
}, [projectId, userId, data, columnColors, cloudColors, initialProjectTitle]);

useEffect(() => {
  if (!projectId && !localStorage.getItem("projectId")) return;
  if (!hasStartedEditing) return;
  if (isLoading) return;

  const timer = setTimeout(() => {
    autoSaveDraft();
  }, 1000);

  return () => clearTimeout(timer);
}, [autoSaveDraft, projectId, hasStartedEditing, isLoading]);

  // Save project data to backend
  const handlePublish = async () => {
    // Validate form before saving
    if (!isFormValid()) {
      const emptyFields = getEmptyFields();
      const fieldList =
        emptyFields.length <= 3
          ? emptyFields.join(", ")
          : `${emptyFields.slice(0, 2).join(", ")}, and ${
              emptyFields.length - 2
            } more`;

      setToast({
        message: `Please fill in all required fields: ${fieldList}`,
        type: "warning",
      });
      return;
    }

    setIsSaving(true);
    const isNameChange =
      data.projectTitle.trim() !== initialProjectTitle.trim();
    try {
      // Helper to parse field values that might be JSON arrays
      // const parseArrayField = (value: string): string[] => {
      //   if (!value) return [];
      //   try {
      //     const parsed = JSON.parse(value);
      //     return Array.isArray(parsed) ? parsed : [value];
      //   } catch {
      //     return value
      //       .split(",")
      //       .map((s) => s.trim())
      //       .filter((s) => s);
      //   }
      // };

      const puzzleColorsForSave = buildPuzzleColorsForSave(
        cloudColors,
        data.externalInfluences
      ).map((c) => ({
        bg: normalizeHex(c.bg),
        text: normalizeHex(c.text),
      }));

      const tocColorPayload = buildTocColorPayload(
        {
          goal: {
            bg: normalizeHex(columnColors.goal?.bg || "#ffffff"),
            text: normalizeHex(columnColors.goal?.text || "#111111"),
          },
          aim: {
            bg: normalizeHex(columnColors.aim?.bg || "#ffffff"),
            text: normalizeHex(columnColors.aim?.text || "#111111"),
          },
          objectives: {
            bg: normalizeHex(columnColors.objectives?.bg || "#ffffff"),
            text: normalizeHex(columnColors.objectives?.text || "#111111"),
          },
          activities: {
            bg: normalizeHex(columnColors.activities?.bg || "#ffffff"),
            text: normalizeHex(columnColors.activities?.text || "#111111"),
          },
        },
        puzzleColorsForSave
      );

      const payload = {
        userId,
        projectId: currentProjectId,
        projectTitle: data.projectTitle,
        updateName: isNameChange,
        status: "published" as "draft" | "published",
        tocData: buildTocDataPayload(
          {
            goal: data.goal || "",
            aim: data.aim || "",
            objectives: data.objectives,
            activities: data.activities,
            externalInfluences: data.externalInfluences,
          },
          data.projectTitle
        ),
        tocColor: tocColorPayload,
      };

      const result = await updateToc(payload);
      if (result.success) {
        if (isNameChange) {
          setInitialProjectTitle(data.projectTitle.trim());
        }
        setToast({ message: "Form saved successfully!", type: "success" });
      } else {
        setToast({
          message: `Error saving form: ${result.message}`,
          type: "error",
        });
        console.error("Save error:", result);
      }
    } catch (err: any) {
      console.error("Save error:", err);
      setToast({
        message: err.response?.data?.message || "Unexpected error occurred",
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleExpertReviewRequest = async () => {
  try {
    await requestExpertReview(currentProjectId);
    setToast({
      message: "Expert review request sent successfully!",
      type: "success",
    });
  } catch (err: any) {
    console.error("Expert review request error:", err);
    setToast({
      message:
        err.response?.data?.message ||
        "Failed to send expert review request.",
      type: "error",
    });
  }
};

  const canSave = isFormValid();

  return (
    <div>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="app-header">
        <button
          className="back-dashboard-btn"
          onClick={() => navigate("/project")}
        >
          ← Back to Dashboard
        </button>
        <h1>Impact Alignment Map<sup className="tm">TM</sup></h1>
      </div>

      <div className="autosave-status" aria-live="polite">
        {saveStatus === "dirty" && <span>Unsaved changes</span>}
        {saveStatus === "saving" && <span>Saving draft...</span>}
        {saveStatus === "saved" && <span>Draft saved at {lastSavedAt}</span>}
        {saveStatus === "error" && <span>Autosave failed</span>}
      </div>

      <div className="app-container">
        {/* Left panel - Form inputs */}
        <FormPanel
          data={data}
          updateField={updateField}
          highlightField={highlightField}
        />

        {/* Right panel - Visual diagram */}
        <div className="right-panel-wrapper">
          <VisualPanel
            data={data}
            updateField={updateField}
            columnColors={columnColors}
            cloudColors={cloudColors}
            updateColumnColors={updateColumnColors}
            updateCloudColors={updateCloudColors}
            onFieldAdded={handleFieldAdded}
            isLoading={isLoading}
          />

          

          {/* Save button - disabled if form is invalid */}
          <button
            className="btn-save"
            onClick={handlePublish}
            disabled={isSaving || !canSave}
            title={!canSave ? "Please fill in all required fields" : ""}
          >
            {isSaving ? (
              <>
                <span className="spinner"></span> Saving...
              </>
            ) : (
              <>Complete</>
            )}
          </button>

<div className="expert-review-section">
             <p>
              Not sure if your program’s impact would “stand up” in front of funders or executives?
             </p>
            <button
            type="button"
            className="btn-expert-review"
            onClick={handleExpertReviewRequest}
            >
            Book an Expert Review
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

export default App;
